import { DEPLOYMENT_CENTER_TABS, DeploymentCenterTab } from '@pubstack/common/src/deployment-center'
import { TrackingLabel } from '~/components/layout/ProductTrackingIntegration'

/**
 * The deployment center holds One's stacks and Node's in one work queue, split by **what has to happen to
 * a stack** rather than by which product it belongs to. Which tab a stack lands on is the backend's
 * derivation (`stackGroupStateOf`); which tabs exist at all is decided here rather than inline in the
 * page — the page reads a dozen hooks and none of this depends on any of them.
 */

/**
 * The tab bar takes plain strings and hands a clicked tab back **by label**, so the mapping has to be
 * reversible and lives next to the tabs themselves.
 *
 * "Ready to deploy" rather than "To deploy": it is the wording `StackStatus` already puts on a
 * `to-deploy` stack, and the bar should not rename what the status pill in its own rows calls it.
 */
export const DEPLOYMENT_TAB_LABEL: Record<DeploymentCenterTab, string> = {
  'to-configure': 'To configure',
  'out-of-sync': 'Out of sync',
  'to-deploy': 'Ready to deploy',
}

export const deploymentTabOfLabel = (label: string): DeploymentCenterTab | undefined => (Object.keys(DEPLOYMENT_TAB_LABEL) as DeploymentCenterTab[]).find((tab) => DEPLOYMENT_TAB_LABEL[tab] === label)

/**
 * One label per tab, spelled out rather than interpolated: `TrackingLabel` is a compile-checked template
 * type, and a computed prefix defeats the check that keeps these valid.
 *
 * `common-` because the page is product-neutral. The old page built `${product}-deployment-center-…` from
 * the URL, which a neutral path cannot answer — see `useProduct`'s own warning about its `adm` fallback.
 */
export const DEPLOYMENT_TAB_TRACKING: Record<DeploymentCenterTab, TrackingLabel> = {
  'to-configure': 'common-deployment-center-to-configure-tab-apply',
  'out-of-sync': 'common-deployment-center-out-of-sync-tab-apply',
  'to-deploy': 'common-deployment-center-to-deploy-tab-apply',
}

/**
 * `hasNodeAccess` is the `pbsStandalone` **access** feature: the customer bought Node, so Node stacks are
 * theirs and so is the To configure tab.
 */
export type DeploymentTabContext = {
  hasNodeAccess: boolean
}

/**
 * Which tabs the bar shows.
 *
 * Out of sync and Ready to deploy are always there: they list whatever stacks the viewer is allowed to
 * see, of either product, and a scope on One alone still has One stacks to update and deploy.
 *
 * To configure is different — it is the one tab that *creates* stacks, and the only stack it knows how to
 * create in bulk is a Node `Default` one (`createDefaultStacks` is `pbs`-only; a One stack needs ad units
 * and header-bidding settings, so an empty one would deploy nothing). Showing it to a scope that runs no
 * Node stacks would therefore offer an action that cannot apply to anything it owns.
 */
export const visibleDeploymentTabs = ({ hasNodeAccess }: DeploymentTabContext): DeploymentCenterTab[] => DEPLOYMENT_CENTER_TABS.filter((tab) => tab !== 'to-configure' || hasNodeAccess)
